/**
 * Pure header-merging logic for the API client request layer.
 *
 * Lives in a side-effect-free module so unit tests can exercise the
 * merge contract without triggering client.ts's import-time read of
 * `VITE_API_BASE_URL` (which throws under vitest where the env is not
 * loaded). client.ts re-exports `buildRequestHeaders` so production
 * callers continue importing from one place.
 */

/**
 * Merge headers for an outbound API request.
 *
 * Order matters:
 *   1. `Content-Type: application/json` default
 *   2. caller's `optionsHeaders` (per-request overrides)
 *   3. `extraHeaders` from the registered extra-headers provider
 *   4. `Authorization: Bearer <token>` applied last so it wins on
 *      collision — prevents the extra-headers surface (the scope-
 *      override toggle, etc.) from being used to spoof auth.
 *
 * Returns the merged header object — caller passes it to fetch().
 */
export function buildRequestHeaders(
  optionsHeaders: Record<string, string> | undefined,
  extraHeaders: Record<string, string>,
  token: string | null,
): Record<string, string> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(optionsHeaders ?? {}),
    ...extraHeaders,
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}
